import type { RegistrySlot } from "./types";

export type HistoryView = "designation" | "base" | "additive" | "color" | "none";

export interface CodeLabels {
  designationCode?: string | null;
  baseCode?: string | null;
  additiveCode?: string | null;
  colorCode?: string | null;
}

export interface HistoryViewSpec {
  key: HistoryView;
  label: (resinFamilyCode: string) => string;
  slot: RegistrySlot | null;
  codeOf: (labels: CodeLabels) => string | null | undefined;
}

export const HISTORY_VIEWS: HistoryViewSpec[] = [
  { key: "designation", label: () => "Resin code", slot: null, codeOf: (labels) => labels.designationCode },
  { key: "base", label: () => "Base", slot: "Base", codeOf: (labels) => labels.baseCode },
  { key: "additive", label: () => "Additive", slot: "Additive", codeOf: (labels) => labels.additiveCode },
  { key: "color", label: () => "Color", slot: "Color", codeOf: (labels) => labels.colorCode },
  { key: "none", label: (resinFamilyCode) => `All ${resinFamilyCode}`, slot: null, codeOf: () => null },
];

export interface HistoryGroup<Item> {
  code: string;
  items: Item[];
}

export function primaryView(labels: CodeLabels): HistoryView {
  return HISTORY_VIEWS.find((view) => view.key !== "none" && view.codeOf(labels))?.key ?? "none";
}

export function itemsInView<Item extends CodeLabels>(items: Item[], view: HistoryView): Item[] {
  return items.filter((item) => primaryView(item) === view);
}

export function groupByView<Item extends CodeLabels>(items: Item[], view: HistoryView): HistoryGroup<Item>[] {
  const viewSpec = HISTORY_VIEWS.find((candidate) => candidate.key === view)!;
  const groups = new Map<string, HistoryGroup<Item>>();
  for (const item of itemsInView(items, view)) {
    const code = viewSpec.codeOf(item) ?? "";
    if (!groups.has(code)) groups.set(code, { code, items: [] });
    groups.get(code)!.items.push(item);
  }
  return [...groups.values()];
}

export function countsByView(items: CodeLabels[]): Record<HistoryView, number> {
  const counts: Record<HistoryView, number> = { designation: 0, base: 0, additive: 0, color: 0, none: 0 };
  for (const item of items) counts[primaryView(item)] += 1;
  return counts;
}
